if (!customElements.get('hero-slider')) {
  class HeroSlider extends SwiperComponent {
    constructor() {
      super();
      this.lastActiveIndex = null;
      this.onWindowLoadInit = () => this.init();
      this.onSectionLoadInit = () => this.init();
      this.onSectionSelectInit = () => this.init();
      this.onShopifyBlockSelect = e => {
        if (this.swiper && e.target.closest('hero-slider') === this) {
          const selectedSlideIndex = +e.target.dataset.index;
          this.swiper.slideTo(selectedSlideIndex, 600);
        }
      };
      this.onKeyboardNavigation = event => {
        if (!this.isInViewport()) return;

        if (event.key === 'ArrowRight') {
          const nextButton = this.querySelector('.swiper-button--next');
          if (nextButton) {
            nextButton.click();
          }
        }
        if (event.key === 'ArrowLeft') {
          const prevButton = this.querySelector('.swiper-button--prev');
          if (prevButton) {
            prevButton.click();
          }
        }
      };
      this.cleanupDesktopPriorityListeners = null;
      this.cleanupMobilePriorityListeners = null;
      this.lcpFallbackTimeoutId = null;
      this.lcpObserver = null;
    }

    connectedCallback() {
      // Override parent's connectedCallback
      // We'll use SwiperComponent's init method
      this.swiperOptions = this.buildHeroSliderOptions();
      this.swiperInitialized = false;
      this.section = this.closest('section');
      this.observer = null;

      // Initialize swiper (using SwiperComponent's logic)
      if (this.section && this.section.classList.contains('shopify-section')) {
        this.observer = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.init();
              observer.disconnect();
            }
          });
        });
        this.observer.observe(this.section);
      }

      if (Shopify.designMode) {
        window.addEventListener('shopify:section:load', this.onSectionLoadInit);
        window.addEventListener('shopify:section:select', this.onSectionSelectInit);

        setTimeout(() => {
          if (!this.swiperInitialized) this.init();
        }, 100);
      } else {
        window.addEventListener('load', this.onWindowLoadInit);
      }

      // Hero-specific event listeners
      window.addEventListener('shopify:block:select', this.onShopifyBlockSelect);

      this.initDeferredDesktopVideos();
      this.initDeferredMobileVideos();
      this.addKeyboardNavigation();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (Shopify.designMode) {
        window.removeEventListener('shopify:section:load', this.onSectionLoadInit);
        window.removeEventListener('shopify:section:select', this.onSectionSelectInit);
      } else {
        window.removeEventListener('load', this.onWindowLoadInit);
      }
      window.removeEventListener('shopify:block:select', this.onShopifyBlockSelect);
      document.removeEventListener('keydown', this.onKeyboardNavigation);
      this.cleanupDesktopPriorityListeners?.();
      this.cleanupMobilePriorityListeners?.();

      if (this.lcpFallbackTimeoutId) {
        window.clearTimeout(this.lcpFallbackTimeoutId);
        this.lcpFallbackTimeoutId = null;
      }
      this.lcpObserver?.disconnect();
      this.lcpObserver = null;
    }

    initDeferredDesktopVideos() {
      if (!window.matchMedia('(min-width: 750px)').matches) return;

      const deferredContainers = this.querySelectorAll('[data-desktop-deferred-hero]');
      if (!deferredContainers.length) return;

      const runWhenIdle = callback => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(callback, { timeout: 2000 });
        } else {
          window.setTimeout(callback, 1200);
        }
      };

      const startVideos = () => {
        const activeSlide = this.swiper?.wrapperEl?.querySelector(`[data-index="${this.swiper.activeIndex}"]`);
        const activeContainer = activeSlide?.querySelector('[data-desktop-deferred-hero]') || deferredContainers[0];
        this.hydrateDeferredVideo(activeContainer, 'desktop');
      };

      const priorityContainer =
        this.swiper?.wrapperEl?.querySelector(`[data-index="${this.swiper?.activeIndex || 0}"] [data-desktop-deferred-hero][data-hero-priority="true"]`) ||
        deferredContainers[0]?.matches('[data-hero-priority="true"]') && deferredContainers[0];
      if (priorityContainer) {
        // Avoid scheduling multiple delayed starts on each slideChange/init pass.
        if (priorityContainer.dataset.heroDeferredInit === 'true') {
          return;
        }
        priorityContainer.dataset.heroDeferredInit = 'true';

        // Keep poster as initial LCP candidate on desktop.
        // Start hero video after first interaction or a delayed idle fallback.
        priorityContainer.dataset.heroDeferredDelay = 'true';
        const runPriorityStart = () => this.hydrateDeferredVideo(priorityContainer, 'desktop');
        let started = false;
        const startOnce = () => {
          if (started) return;
          started = true;
          priorityContainer.dataset.heroDeferredDelay = 'false';
          cleanupInteractionListeners();
          runPriorityStart();
        };

        const interactionEvents = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
        const interactionOptions = { passive: true };
        const onUserInteraction = () => startOnce();
        const cleanupInteractionListeners = () => {
          interactionEvents.forEach(eventName => {
            window.removeEventListener(eventName, onUserInteraction, interactionOptions);
          });
          this.cleanupDesktopPriorityListeners = null;
        };
        this.cleanupDesktopPriorityListeners = cleanupInteractionListeners;
        interactionEvents.forEach(eventName => {
          window.addEventListener(eventName, onUserInteraction, interactionOptions);
        });

        const scheduleDelayedStart = () => {
          // Defer autoplay until after first LCP signal (or timeout fallback).
          this.runAfterFirstLCP(() => runWhenIdle(startOnce), 6000);
        };

        if (document.readyState === 'complete') {
          scheduleDelayedStart();
        } else {
          window.addEventListener('load', scheduleDelayedStart, { once: true });
        }
        return;
      }

      const scheduleStart = () => {
        window.setTimeout(() => runWhenIdle(startVideos), 1200);
      };

      if (document.readyState === 'complete') {
        scheduleStart();
      } else {
        window.addEventListener('load', scheduleStart, { once: true });
      }
    }

    initDeferredMobileVideos() {
      if (!window.matchMedia('(max-width: 749px)').matches) return;

      const deferredContainers = this.querySelectorAll('[data-mobile-deferred-hero]');
      if (!deferredContainers.length) return;

      const runWhenIdle = callback => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(callback, { timeout: 2000 });
        } else {
          window.setTimeout(callback, 1200);
        }
      };

      const startVideos = () => {
        const activeSlide = this.swiper?.wrapperEl?.querySelector(`[data-index="${this.swiper.activeIndex}"]`);
        const activeContainer = activeSlide?.querySelector('[data-mobile-deferred-hero]') || deferredContainers[0];
        this.hydrateDeferredVideo(activeContainer, 'mobile');
      };

      const priorityContainer =
        this.swiper?.wrapperEl?.querySelector(`[data-index="${this.swiper?.activeIndex || 0}"] [data-mobile-deferred-hero][data-hero-priority="true"]`) ||
        deferredContainers[0]?.matches('[data-hero-priority="true"]') && deferredContainers[0];
      if (priorityContainer) {
        // Avoid scheduling multiple delayed starts on each slideChange/init pass.
        if (priorityContainer.dataset.heroDeferredInit === 'true') {
          return;
        }
        priorityContainer.dataset.heroDeferredInit = 'true';

        // Keep poster as initial LCP candidate on mobile.
        // Start hero video after first interaction or a delayed idle fallback.
        priorityContainer.dataset.heroDeferredDelay = 'true';
        const runPriorityStart = () => this.hydrateDeferredVideo(priorityContainer, 'mobile');
        let started = false;
        const startOnce = () => {
          if (started) return;
          started = true;
          priorityContainer.dataset.heroDeferredDelay = 'false';
          cleanupInteractionListeners();
          runPriorityStart();
        };

        const interactionEvents = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
        const interactionOptions = { passive: true };
        const onUserInteraction = () => startOnce();
        const cleanupInteractionListeners = () => {
          interactionEvents.forEach(eventName => {
            window.removeEventListener(eventName, onUserInteraction, interactionOptions);
          });
          this.cleanupMobilePriorityListeners = null;
        };
        this.cleanupMobilePriorityListeners = cleanupInteractionListeners;
        interactionEvents.forEach(eventName => {
          window.addEventListener(eventName, onUserInteraction, interactionOptions);
        });

        const scheduleDelayedStart = () => {
          // Defer autoplay until after first LCP signal (or timeout fallback).
          this.runAfterFirstLCP(() => runWhenIdle(startOnce), 4500);
        };

        if (document.readyState === 'complete') {
          scheduleDelayedStart();
        } else {
          window.addEventListener('load', scheduleDelayedStart, { once: true });
        }
        return;
      }

      const scheduleStart = () => {
        window.setTimeout(() => runWhenIdle(startVideos), 1200);
      };

      if (document.readyState === 'complete') {
        scheduleStart();
      } else {
        window.addEventListener('load', scheduleStart, { once: true });
      }
    }

    runAfterFirstLCP(callback, maxWaitMs = 6000) {
      let hasRun = false;
      let timeoutId = null;

      const runOnce = () => {
        if (hasRun) return;
        hasRun = true;
        if (timeoutId) window.clearTimeout(timeoutId);
        this.lcpFallbackTimeoutId = null;
        this.lcpObserver = null;
        callback();
      };

      timeoutId = window.setTimeout(runOnce, maxWaitMs);
      this.lcpFallbackTimeoutId = timeoutId;

      if (!('PerformanceObserver' in window)) {
        return;
      }

      try {
        const observer = new PerformanceObserver(entryList => {
          const entries = entryList.getEntries();
          if (!entries?.length) return;
          observer.disconnect();
          // Run in next frame so poster paint settles before video hydration.
          window.requestAnimationFrame(runOnce);
        });
        observer.observe({ type: 'largest-contentful-paint', buffered: true });
        this.lcpObserver = observer;
      } catch (error) {
        // Ignore unsupported observer modes and rely on timeout fallback.
      }
    }

    hydrateDeferredVideo(container, mode) {
      if (!container) return;
      container.dataset.heroDeferredDelay = 'false';

      const wrapperSelector = mode === 'desktop' ? '[data-hero-desktop-video-wrapper]' : '[data-hero-mobile-video-wrapper]';
      const posterSelector = mode === 'desktop' ? '[data-hero-desktop-poster]' : '[data-hero-mobile-poster]';
      const videoWrapper = container.querySelector(wrapperSelector);
      const posterWrapper = container.querySelector(posterSelector);
      const video = videoWrapper?.querySelector('video');
      if (!video || !videoWrapper) return;

      if (container.dataset.heroDeferredStarted === 'true') {
        video.play().catch(() => {});
        return;
      }

      const revealVideo = () => {
        if (container.dataset.heroDeferredRevealed === 'true') return;
        container.dataset.heroDeferredRevealed = 'true';
        videoWrapper.classList.remove('is-hidden');
        posterWrapper?.classList.add('is-hidden');
      };

      container.dataset.heroDeferredStarted = 'true';
      video.addEventListener('loadeddata', revealVideo, { once: true });
      video.addEventListener('playing', revealVideo, { once: true });
      video.addEventListener('canplay', () => {
        video.play().catch(() => {});
      }, { once: true });
      video.preload = 'metadata';
      this.ensureVideoSources(video);
      video.play().catch(() => {});
    }

    ensureVideoSources(video) {
      if (!video) return;
      let hasChanges = false;
      video.querySelectorAll('source[data-src]').forEach(source => {
        if (!source.getAttribute('src')) {
          source.setAttribute('src', source.dataset.src);
          hasChanges = true;
        }
      });
      if (hasChanges) {
        video.load();
        video.dataset.heroLoaded = 'true';
      }
    }

    syncActiveSlideVideos(swiper) {
      const slides = swiper?.wrapperEl?.querySelectorAll('.swiper-slide');
      if (!slides?.length) return;
      const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;

      slides.forEach((slide, index) => {
        const isActive = index === swiper.activeIndex;
        slide.querySelectorAll('video').forEach(video => {
          if (!this.isVideoForCurrentViewport(video, isMobileViewport)) {
            video.pause();
            return;
          }

          const deferredContainer = video.closest('[data-desktop-deferred-hero], [data-mobile-deferred-hero]');
          if (deferredContainer?.dataset.heroDeferredDelay === 'true') {
            return;
          }

          if (isActive) {
            if (video.preload === 'none') {
              video.preload = 'metadata';
            }
            this.ensureVideoSources(video);
            if (video.dataset.heroLoaded !== 'true') {
              video.load();
              video.dataset.heroLoaded = 'true';
            }
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      });
    }

    isVideoForCurrentViewport(video, isMobileViewport) {
      const hiddenOnMobile = video.closest('.small-hide');
      if (hiddenOnMobile && isMobileViewport) return false;

      const hiddenOnDesktop = video.closest('.small-up-hide');
      if (hiddenOnDesktop && !isMobileViewport) return false;

      return true;
    }

    buildHeroSliderOptions() {
      const autoplayOptions = {
        delay: this.dataset.autoplayInterval || 5000
      };

      const useAutoHeight = this.dataset.autoHeight === 'true';

      return {
        rewind: true,
        slidesPerView: 1,
        speed: 600,
        followFinger: false,
        ...(useAutoHeight
          ? {
              autoHeight: true,
              observer: true,
              observeParents: true,
              observeSlideChildren: true
            }
          : {}),
        navigation: {
          nextEl: '.swiper-button--next',
          prevEl: '.swiper-button--prev'
        },
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
          renderBullet: function (i, className) {
            return `
              <button class="${className}">
                <span>0${i + 1}</span>
                <svg class="square-progress" width="26" height="26">
                  <rect class="square-origin" width="26" height="26" rx="5" ry="5" />
                </svg>
                <svg class="progress" width="1.8rem" height="1.8rem" style="inset-inline-start: ${0 - (i * 2.4 + 3.4)}rem">
                  <circle class="circle-origin" r="0.8rem" cx="0.95rem" cy="0.95rem"></circle>
                </svg>
              </button>
            `;
          }
        },
        autoplay: this.dataset.autoplay === 'true' ? autoplayOptions : false,
        on: {
          init: swiper => {
            this.handleSlideChange(swiper);
            if (useAutoHeight) {
              requestAnimationFrame(() => swiper.updateAutoHeight?.(0));
            }
          },
          slideChange: swiper => {
            this.handleSlideChange(swiper);
            if (useAutoHeight) {
              requestAnimationFrame(() => swiper.updateAutoHeight?.(300));
            }
          }
        }
      };
    }

    init() {
      if (this.swiperInitialized) return;
      this.swiperInitialized = true;

      // Use custom Swiper options for HeroSlider
      this.swiper = new Swiper(this, this.swiperOptions);

      if (this.dataset.autoHeight === 'true') {
        const bumpAutoHeight = () => this.swiper?.updateAutoHeight?.(0);
        this.querySelectorAll('img').forEach(img => {
          if (img.complete) return;
          img.addEventListener('load', bumpAutoHeight, { once: true });
          img.addEventListener('error', bumpAutoHeight, { once: true });
        });
      }

      // Maintain slider property for backward compatibility
      this.slider = this.swiper;
    }

    handleSlideChange(swiper) {
      this.handleSlideChangeAnimations(swiper);
      this.syncActiveSlideVideos(swiper);
      this.initDeferredDesktopVideos();
      this.initDeferredMobileVideos();

      /** change header menu color for active slide */
      let headerInner = document.querySelector('.header__inner');
      let heroInners = document.querySelectorAll('.hero__inner');

      if (!headerInner || !heroInners) {
        return;
      }

      // change --transparent-header-menu-text-color value on document style attributes
      document.documentElement.style.setProperty(
        '--transparent-header-menu-text-color',
        heroInners[swiper.activeIndex].dataset.headerMenuTextColor
      );
    }
    /**
     * get direction & set custom transitions
     * with delay for content animation items
     * */
    handleSlideChangeAnimations(swiper) {
      let delay = 300;
      const queryElement = `[data-index="${swiper.activeIndex}"]`;
      const queryArray = [...(swiper.wrapperEl.querySelector(queryElement).querySelectorAll('.hero__animation') || [])];
      if (!queryArray.length) {
        return;
      }
      const allowMobileAnimation = swiper.wrapperEl.closest('.hero__content')?.dataset.animationMobile === 'true';
      if (!allowMobileAnimation && DeviceDetector.isMobile()) {
        queryArray.forEach(element => {
          element.classList.remove('in-delay');
        });
        return;
      }
      // set all element in delay
      queryArray.forEach(element => {
        element.classList.add('in-delay');
      });

      // get direction and set transitions
      if (this.lastActiveIndex > swiper.activeIndex) {
        delay = 650 + delay;
        swiper.wrapperEl.style.transitionDuration = '1000ms';
        swiper.wrapperEl.style.transitionTimingFunction = 'cubic-bezier(0.45, 0.00, 0.15, 0.95)';
      } else if (this.lastActiveIndex < swiper.activeIndex) {
        delay = 1000 + delay;
        swiper.wrapperEl.style.transitionDuration = '1000ms';
        swiper.wrapperEl.style.transitionTimingFunction = 'cubic-bezier(0.45, 0.00, 0.15, 0.95)';
      }

      // set delay calculated per item
      setTimeout(() => {
        if (this.lastActiveIndex > swiper.activeIndex) {
          delay = 325 + delay;
        } else if (this.lastActiveIndex < swiper.activeIndex) {
          delay = 500 + delay;
        }
        queryArray.forEach((element, index) => {
          if (index === 1) {
            delay = delay + 50;
          } else if (index === 2) {
            delay = delay + 175;
          } else if (index === 3) {
            delay = delay + 300;
          }
          element.classList.remove('in-delay');
        });
      }, delay);

      // set last active global
      this.lastActiveIndex = swiper.activeIndex;
    }
    addKeyboardNavigation() {
      document.removeEventListener('keydown', this.onKeyboardNavigation);
      document.addEventListener('keydown', this.onKeyboardNavigation);
    }

    isInViewport() {
      const rect = this.getBoundingClientRect();
      return (
        rect.top < window.innerHeight && // top part in viewport
        rect.bottom > 0 && // bottom part in viewport
        rect.left < window.innerWidth && // left part in viewport
        rect.right > 0 // right part in viewport
      );
    }
  }
  customElements.define('hero-slider', HeroSlider);
}
